import {
  OPERATOR_LABEL,
  type ScoreEntity,
  WHERE_FIELDS,
  type WhereGroup,
  type WhereRelation,
  operatorsFor,
} from '@parcel/filters';
import * as React from 'react';
import { type Field, QueryBuilder, type RuleGroupType, prepareRuleGroup } from 'react-querybuilder';
import type { WhereOptions } from '../where-bar/where-menu.js';
import { emptyRule, isUnpicked } from './criteria-state.js';
import {
  type BuilderContext,
  FieldPicker,
  MAX_NESTING,
  OperatorPicker,
  ParcelRuleGroup,
  RemoveX,
  ValueEditorAdapter,
} from './qb-controls.js';

export interface CriterionBuilderProps {
  entity: ScoreEntity;
  where: WhereGroup;
  onChange: (next: WhereGroup) => void;
  /** field: one rule, the card names the field; relation: that relation's fields; custom: everything, with groups. */
  mode: 'field' | 'relation' | 'custom';
  lockedField?: string;
  relation?: WhereRelation;
  options: WhereOptions;
  disabled?: boolean;
}

/** The value an operator starts from: a list for is / is not, Yes for a yes/no field, else nothing yet. */
function defaultValue(entity: ScoreEntity, key: string, operator: string): unknown {
  if (isUnpicked(key) || operator === 'in' || operator === 'notIn') return [];
  const rule = emptyRule(entity, key);
  return rule.operator === operator ? rule.value : undefined;
}

/** The builder's own keys (ids, value sources) never reach a criterion. */
export function stripBuilderKeys(node: unknown): unknown {
  if (Array.isArray(node)) return node.map(stripBuilderKeys);
  if (!node || typeof node !== 'object') return node;
  const { id: _id, path: _path, valueSource: _vs, ...rest } = node as Record<string, unknown>;
  if (!('rules' in rest)) return rest;
  return { ...rest, rules: (rest.rules as unknown[]).map(stripBuilderKeys) };
}

/** One criterion's rows: react-querybuilder with Parcel's controls and plain operators. */
export function CriterionBuilder({
  entity,
  where,
  onChange,
  mode,
  lockedField,
  relation,
  options,
  disabled,
}: CriterionBuilderProps) {
  const fields = React.useMemo(() => {
    const all = WHERE_FIELDS[entity];
    if (mode === 'relation') return all.filter((f) => f.relation === relation);
    if (mode === 'field') return all.filter((f) => f.key === lockedField);
    // A score never reads its own score.
    return all.filter((f) => f.key !== 'score');
  }, [entity, mode, relation, lockedField]);
  const qbFields: Field[] = React.useMemo(
    () =>
      fields.map((f) => ({
        name: f.key,
        label: f.label,
        operators: operatorsFor(f).map((op) => ({
          name: op,
          value: op,
          label: OPERATOR_LABEL[op],
        })),
      })),
    [fields],
  );
  const context: BuilderContext = React.useMemo(
    () => ({
      entity,
      fields,
      options,
      mode,
      rootRules: where.rules.length,
      where,
      ...(mode === 'field' && lockedField ? { lockedField } : {}),
      ...(disabled ? { disabled } : {}),
    }),
    [entity, fields, options, mode, lockedField, disabled, where],
  );
  // The builder keys rows by id: one prepared query per condition, so a re-render never remounts a row.
  const whereText = JSON.stringify(stripBuilderKeys(where));
  const prepared = React.useRef<{ text: string; query: RuleGroupType } | null>(null);
  if (prepared.current?.text !== whereText) {
    prepared.current = { text: whereText, query: prepareRuleGroup(where as RuleGroupType) };
  }
  return (
    <QueryBuilder
      fields={qbFields}
      query={prepared.current.query}
      onQueryChange={(q) => {
        const next = stripBuilderKeys(q) as WhereGroup;
        const text = JSON.stringify(next);
        // Only a real change reaches the card; the prepared ids stay with it.
        prepared.current = { text, query: q as RuleGroupType };
        if (text !== whereText) onChange(next);
      }}
      // A new row starts with no field: it opens on its field list. A card's rows stay on its relation.
      onAddRule={(rule) =>
        mode === 'field' && lockedField
          ? { ...rule, ...emptyRule(entity, lockedField) }
          : { ...rule, field: relation ? `${relation}.` : '~', operator: 'in', value: [] }
      }
      context={context}
      disabled={disabled}
      controlElements={{
        ruleGroup: ParcelRuleGroup,
        fieldSelector: FieldPicker,
        operatorSelector: OperatorPicker,
        valueEditor: ValueEditorAdapter,
        removeRuleAction: RemoveX,
      }}
      controlClassnames={{ rule: 'flex min-w-0 flex-wrap items-center gap-2' }}
      getDefaultValue={(rule) => defaultValue(entity, rule.field, rule.operator)}
      addRuleToNewGroups
      autoSelectField={false}
      resetOnFieldChange
      resetOnOperatorChange
      listsAsArrays
      // The root plus two nested levels: the API's depth of 3.
      maxLevels={MAX_NESTING}
    />
  );
}
